/**
 * HUD UI scale: "auto" (by viewport height) or a fixed multiplier. Persisted in localStorage
 * (separately from graphics settings); every storage access is in try/catch. The layout shell
 * (layout.ts) subscribes and writes the resolved value to `--hk-scale` on the HUD root.
 *
 * The HUD is designed at 1080p. Auto scales with the viewport height and clamps, then applies
 * UI_SCALE_BASE (20% smaller than the old 1.44 default, user feedback 2026-10-16): 1080p → 1.15,
 * 900p → 0.96, 720p → 0.86, 1440p → 1.53.
 */
import { el } from './dom';

export type UiScaleSetting = 'auto' | number;

export const UI_SCALE_KEY = 'keystone.hudScale.v1';
export const UI_SCALE_RANGE = { min: 0.7, max: 2.5, step: 0.05 } as const;
/** Auto scale: viewport height / design height, clamped. */
export const UI_SCALE_DESIGN_HEIGHT = 1080;
/** min 0.75 keeps the 1.44 base clear of the centre keep-out at 1280×720 (720p resolves to 1.08). */
export const UI_SCALE_AUTO = { min: 0.75, max: 1.33 } as const;
/** Default size multiplier on top of the auto viewport scale. */
export const UI_SCALE_BASE = 1.15;

export function sanitizeUiScale(raw: unknown): UiScaleSetting {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.min(UI_SCALE_RANGE.max, Math.max(UI_SCALE_RANGE.min, raw));
  return 'auto';
}

/** The multiplier for a setting at a viewport height (CSS px). Rounded to 0.01. */
export function resolveUiScale(setting: UiScaleSetting, viewportHeight: number): number {
  if (setting !== 'auto') return sanitizeUiScale(setting) as number;
  const h = Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : UI_SCALE_DESIGN_HEIGHT;
  const k = Math.min(UI_SCALE_AUTO.max, Math.max(UI_SCALE_AUTO.min, h / UI_SCALE_DESIGN_HEIGHT));
  return Math.round(k * UI_SCALE_BASE * 100) / 100;
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadUiScale(storage: Pick<Storage, 'getItem'> | null = defaultStorage()): UiScaleSetting {
  try {
    const raw = storage?.getItem(UI_SCALE_KEY);
    return sanitizeUiScale(raw ? JSON.parse(raw) : null);
  } catch {
    return 'auto';
  }
}

export function saveUiScale(s: UiScaleSetting, storage: Pick<Storage, 'setItem'> | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(UI_SCALE_KEY, JSON.stringify(sanitizeUiScale(s)));
    return true;
  } catch {
    return false;
  }
}

type Listener = (s: UiScaleSetting) => void;

class UiScaleStore {
  private value: UiScaleSetting | null = null;
  private readonly listeners = new Set<Listener>();

  get(): UiScaleSetting {
    return (this.value ??= loadUiScale());
  }

  set(s: UiScaleSetting): void {
    this.value = sanitizeUiScale(s);
    saveUiScale(this.value);
    for (const l of this.listeners) l(this.value);
  }

  /** Calls `fn` now and on every change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }
}

export const uiScaleSettings = new UiScaleStore();

/** Adds a "HUD scale" section (Auto checkbox + slider) to the settings panel. Returns a sync function. */
export function mountUiScaleControls(panel: HTMLElement, before: Node | null = null): () => void {
  const section = el('div', 'hk-scale-settings');
  panel.insertBefore(section, before);
  el('div', 'gfx-title', section, 'Interface');
  const autoRow = el('label', 'gfx-row check', section);
  el('span', '', autoRow, 'HUD scale: auto');
  const auto = el('input', '', autoRow);
  auto.type = 'checkbox';
  const sizeRow = el('label', 'gfx-row', section);
  el('span', '', sizeRow, 'HUD scale');
  const out = el('output', 'gfx-val', sizeRow);
  const range = el('input', '', sizeRow);
  range.type = 'range';
  range.min = String(UI_SCALE_RANGE.min);
  range.max = String(UI_SCALE_RANGE.max);
  range.step = String(UI_SCALE_RANGE.step);
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const sync = (): void => {
    const s = uiScaleSettings.get();
    const v = resolveUiScale(s, typeof window === 'undefined' ? UI_SCALE_DESIGN_HEIGHT : window.innerHeight);
    auto.checked = s === 'auto';
    range.disabled = s === 'auto';
    range.value = String(v);
    out.textContent = s === 'auto' ? `Auto (${pct(v)})` : pct(v);
  };
  auto.addEventListener('change', () => {
    uiScaleSettings.set(auto.checked ? 'auto' : Number(range.value));
    sync();
  });
  range.addEventListener('input', () => {
    uiScaleSettings.set(Number(range.value));
    sync();
  });
  return sync;
}
